import React from 'react';
import { observer } from 'mobx-react';
import { useFormikContext } from 'formik';
import Grid from '@material-ui/core/Grid';

import { useStore } from 'core/store';
import useSession from 'core/hooks/use-session';
import { tooltipHoverTexts } from 'core/helpers/constants';
import { Tooltip } from 'core/components/tooltip';
import { NotificationCard } from 'core/components/notification-card';
import { Label } from 'core/components/label';
import { Card } from 'core/components/card';
import { Button } from 'core/components/button';
import AddButton from 'core/components/add-button';
import RemoveButton from 'core/components/remove-button';

import infoDarkGreen from 'core/assets/svg/info-dark-green.svg';
import savingsOn from 'core/assets/svg/ic-savings-on.svg';
import savingsOff from 'core/assets/svg/ic-savings-off.svg';
import arrowIcon from 'core/assets/svg/up-right-arrow.svg';

import { canReceiveCommunityDriveDiscount } from 'common/components/people/helpers';
import useStyles from './community-drive-card.styles';

const COMMUNITY_DRIVE_APPCUES_ID = '55cbcaec-b292-484b-9210-c6a0162f8b93';

const PolicyCommunityDriveCard = observer(({ fieldName, index, fromPolicy, driver, state }) => {
  const {
    account: accountStore,
    account: {
      policies: { policy: policyStore }
    }
  } = useStore();
  const { isAgency } = useSession();
  const classes = useStyles();
  const { setFieldValue, setFieldTouched, values } = useFormikContext();
  const { isBix } = values;

  // once a driver has community drive once, they have it forever
  const hasCommunityDriveOn = driver?.everCommunityDriveParticipation || driver?.communityDriveParticipation;

  const communityDriveOffered = canReceiveCommunityDriveDiscount({ state, isBix });

  // find the last term where the driver had community drive, return 0 if there is none
  const priorCommunityDriveTerm =
    fromPolicy &&
    policyStore?.policy?.term > 1 &&
    accountStore?.policies?.list?.reduce((term, p) => {
      if (
        p.policyType.includes('A') &&
        p.term < policyStore?.policy?.term &&
        p.term > term &&
        p?.policyDetails?.drivers?.[index]?.communityDriveParticipation
      ) {
        return p.term;
      }
      return term;
    }, 0);

  // if offer, or first term, or priorCommunityDriveTerm is 0, then it's their first term
  const communityDriveFirstTerm =
    !fromPolicy || (fromPolicy && policyStore?.policy?.term === 1) || (fromPolicy && priorCommunityDriveTerm < 1);

  // always show in offer
  // dont show in agency policy except when its already on
  // this is to prevent agency adding community drive mid term
  const showCommunityDrive =
    communityDriveOffered && (!fromPolicy || !isAgency || (isAgency && fromPolicy && hasCommunityDriveOn));

  // Score is based on current policy & driving is based on previous term, default to 0 which means they dont have enough drives
  const currentTermScore =
    (fromPolicy && !communityDriveFirstTerm && priorCommunityDriveTerm > 0 && driver?.communityDriveScore) || 0;

  const onRemove = () => {
    setFieldValue(`${fieldName}.communityDriveParticipation`, false);
    setFieldTouched(`${fieldName}.communityDriveParticipation`, true);
  };

  const onAdd = () => {
    setFieldValue(`${fieldName}.communityDriveParticipation`, true);
    setFieldTouched(`${fieldName}.communityDriveParticipation`, true);
  };

  return showCommunityDrive ? (
    <Grid container className={classes.communityDriveAvailable}>
      <Card type={hasCommunityDriveOn ? 'outlineBeige' : 'light'} className={classes.card}>
        <Grid className={classes.columnContainer}>
          <Grid container className={classes.communityDriveInfoContainer}>
            <Grid item className={classes.labelAndToolTip}>
              <img
                className={classes.savingsIcon}
                src={hasCommunityDriveOn ? savingsOn : savingsOff}
                alt="Savings Icon"
              />
              <Label type="notificationText">
                {communityDriveFirstTerm ? 'Community Drive participation discount' : 'Community Drive discount'}
                <Button
                  color="secondary"
                  variant="text"
                  className={classes.info}
                  onClick={() => window?.Appcues?.show(COMMUNITY_DRIVE_APPCUES_ID)}
                >
                  <img src={arrowIcon} alt="arrow" className={classes.arrow} />
                </Button>
              </Label>
            </Grid>
            <Grid className={communityDriveFirstTerm ? classes.scoreContainer1stTerm : classes.scoreContainer2ndTerm}>
              {hasCommunityDriveOn && fromPolicy && <Label type="green75small">Prior term score</Label>}
              {/* if we have community drive, display NA or score based on if its the first term of community drive*/}
              {hasCommunityDriveOn &&
                fromPolicy &&
                (communityDriveFirstTerm ? (
                  <Grid item className={classes.noScore}>
                    <Label type="green15smallBold">N/A</Label>
                    <Tooltip text={tooltipHoverTexts.communityDriveNoScore} />
                  </Grid>
                ) : (
                  <Label className={classes.scoreSpacer} type="green15smallBold">
                    {currentTermScore > 40 ? currentTermScore : 'Below 41'}/100
                  </Label>
                ))}
              {communityDriveFirstTerm &&
                (hasCommunityDriveOn ? (
                  <RemoveButton className={classes.noBoxShadow} onClick={onRemove} />
                ) : (
                  <AddButton type="full" className={classes.noBoxShadow} onClick={onAdd} />
                ))}
            </Grid>
          </Grid>

          {!communityDriveFirstTerm && currentTermScore < 41 && (
            <NotificationCard
              header={currentTermScore > 0 ? 'Reduced savings' : 'Insufficient drives'}
              icon={infoDarkGreen}
              iconByHeader
              type="light"
              iconSize="icon16"
              className={classes.noBoxShadow}
            >
              <Grid className={classes.notificationCard}>
                {currentTermScore > 0
                  ? 'This driver did not contribute to the Community Drive discount this term —their prior term Community Drive score did not meet the 41 score minimum.'
                  : 'This driver is participating in Community Drive but did not record enough drives in the prior term to contribute to the discount. To receive a discount at the next renewal, the driver must turn on trip recording in the app and complete at least 5 trips within 91 days.'}
              </Grid>
            </NotificationCard>
          )}
        </Grid>
      </Card>
    </Grid>
  ) : (
    <Grid className={classes.communityDriveNotOffered}>
      <NotificationCard type="notificationContentContainer" icon={infoDarkGreen} noBackground iconSize="icon16">
        <Label type="notificationText">
          {communityDriveOffered
            ? 'This driver did not enroll in Community Drive program at purchase. If they activated it in the app and wish to participate in the discount, please contact Agency support.'
            : 'We do not currently offer the Community Drive discount in this state.'}
        </Label>
      </NotificationCard>
    </Grid>
  );
});

export default PolicyCommunityDriveCard;
